import { isPlatformBrowser, Location } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from 'environments/environment';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class LoginService {
    tokenInscri: string;
    keycloak_serveur = environment.keycloak_serveur;
    keycloak_host = environment.keycloak_host;

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        public location: Location,
        private http: HttpClient,
        private cookieService: SsrCookieService
    ) {}

    // Login Method
    public login(user: string, password: string): Observable<any> {
        const headers = new HttpHeaders().set(
            'Content-Type',
            'application/x-www-form-urlencoded'
        );

        const body = new HttpParams()
            .set('grant_type', 'password')
            .set('username', user)
            .set('password', password)
            .set('client_id', 'irbe7-frontend');

        return this.http
            .post(
                this.keycloak_host +
                    '/realms/irbeh/protocol/openid-connect/token',
                body,
                { headers }
            )
            .pipe(
                catchError((error) => {
                    console.error('Error during login:', error);
                    return throwError(() => new Error('Login failed'));
                })
            );
    }

    public signup(
        user: string,
        password: string,
        email: string
    ): Observable<any> {
        const headers = new HttpHeaders().set(
            'Content-Type',
            'application/x-www-form-urlencoded'
        );

        const body = new HttpParams()
            .set('grant_type', 'client_credentials')
            .set('client_id', 'admin-cli')
            .set('client_secret', 'QulDLyuatAKCE9KUGMFohcuSLAIyL0XM');

        return this.http
            .post(
                `${this.keycloak_host}/realms/irbeh/protocol/openid-connect/token`,
                body,
                { headers }
            )
            .pipe(
                switchMap((tokenResponse: any) => {
                    const tokenInscri = tokenResponse.access_token;
                    console.log('Admin Token:', tokenInscri); // Debugging purpose

                    if (!tokenInscri || tokenInscri.length < 20) {
                        console.error('Invalid token received');
                        return throwError(() => new Error('Invalid token'));
                    }

                    const newUser = {
                        enabled: true,
                        username: user,
                        email: email,
                        credentials: [
                            {
                                type: 'password',
                                value: password,
                                temporary: false,
                            },
                        ],
                    };

                    const authHeaders = new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${tokenInscri}`,
                    });

                    // Log the token to verify it's correct before making the second request
                    console.log(
                        'Sending Admin Token for user creation:',
                        tokenInscri
                    );

                    return this.http
                        .post(
                            `${this.keycloak_host}/admin/realms/irbeh/users`,
                            newUser,
                            {
                                headers: authHeaders,
                            }
                        )
                        .pipe(
                            catchError((error) => {
                                console.error(
                                    'Error during user creation:',
                                    error
                                );
                                return throwError(
                                    () => new Error('User creation failed')
                                );
                            })
                        );
                }),
                catchError((error) => {
                    console.error('Error fetching token:', error);
                    return throwError(() => new Error('Token fetch failed'));
                })
            );
    }

    public logout(): void {
        this.cookieService.delete('token');
        sessionStorage.clear();
        location.reload();
    }

    public changePassword(password: string) {
        let token = this.cookieService.get('token');
        const payload = token.split('.')[1];
        const payloadDecodedJson = atob(payload);
        const payloadDecoded = JSON.parse(payloadDecodedJson);
        const id = payloadDecoded.sub;

        const headers = new HttpHeaders().set(
            'Content-Type',
            'application/x-www-form-urlencoded;'
        );

        const body = new HttpParams()
            .set('grant_type', 'client_credentials')
            .set('client_secret', 'QulDLyuatAKCE9KUGMFohcuSLAIyL0XM')
            .set('client_id', 'admin-cli');

        const res = this.http
            .post(
                this.keycloak_host +
                    '/realms/irbeh/protocol/openid-connect/token',
                body,
                { headers: headers }
            )
            .pipe(
                switchMap((response: any) => {
                    token = response.access_token;
                    const reqHeader = new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    });
                    const body2 = {
                        type: 'password',
                        temporary: false,
                        value: password,
                    };
                    return this.http.put(
                        this.keycloak_host +
                            `/admin/realms/irbeh/users/${id}/reset-password`,
                        body2,
                        { headers: reqHeader }
                    );
                })
            );
        return res;
    }

    public getIsLogged() {
        if (isPlatformBrowser(this.platformId)) {
            const token = this.cookieService.get('token');
            return !!token; // This will return true if token is a non-empty string, otherwise false
        }
    }

    public getUsername(): string {
        try {
            if (!this.getIsLogged()) {
                return null;
            }

            const token = this.cookieService.get('token');
            if (!token) {
                return null;
            }

            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }

            const payload = parts[1];
            if (!payload) {
                return null;
            }

            const base64 = this.base64urlToBase64(payload);
            const payloadDecodedJson = atob(base64);
            const payloadDecoded = JSON.parse(payloadDecodedJson);
            return payloadDecoded.preferred_username;
        } catch (e) {
            return null;
        }
    }

    private base64urlToBase64(base64url: string): string {
        return base64url
            .replace(/-/g, '+')
            .replace(/_/g, '/')
            .padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), '=');
    }

    public getIsAdmin(): boolean {
        const token = this.cookieService.get('token');
        const payload = token.split('.')[1];
        const payloadDecodedJson = atob(payload);
        const payloadDecoded = JSON.parse(payloadDecodedJson);
        // console.log(payloadDecoded.realm_access.roles);
        return payloadDecoded.realm_access.roles.indexOf('realm-admin') !== -1;
    }
}
